import React, { useState, useEffect, useRef, RefObject } from 'react';
import Select from 'react-select';
import { ProductsCatalogsViewModel } from 'Common/ViewModels/Catalogs/Products/ProductsCatalogsViewModel';
import { DeliveryUnits, ProductStatus, Currencies, WeightUnits, Units } from 'Common/constants/Enums';
import { File, RefreshCcw, UploadCloud } from 'lucide-react';
import Dropzone from 'react-dropzone';
import { Link } from 'react-router-dom';
import { axios_helper_async } from 'helpers/Axios';
import { Options } from 'Common/Models/Options';
import { WithNotificationsProps } from 'Common/withNotifications';
import { WithTranslationProps } from 'Common/Components/Translation/WithTranslationProps';
import { MessageType } from 'Common/Components/Messages/messageTypes';
import { NotificationType } from 'Common/Components/Notifications/notificationTypes';
import withTranslation from 'Common/withTranslation';
import withNotifications from 'Common/withNotifications';

const modelFields: Record<keyof ProductsCatalogsViewModel, string> = {
    id: 'id',
    brand: 'brand',
    category: 'category',
    // currency: 'currency',
    datasheetUrl: 'datasheetUrl',
    datasheetFile: 'datasheetFile',
    deliveryTime: 'deliveryTime',
    deliveryUnit: 'deliveryUnit',
    description: 'description',
    filterBrand: 'filterBrand',
    filterCategory: 'filterCategory',
    filterProduct: 'filterProduct',
    image: 'image',
    name: 'name',
    price: 'price',
    quantity: 'quantity',
    sku: 'sku',
    status: 'status',
    unit: 'unit',
    weight: 'weight',
    weightUnit: 'weightUnit',
    filterBrands: 'filterBrands',
    filterCategories: 'filterCategories',
    filterProducts: 'filterProducts',
    brands: 'brands',
    categories: 'categories'
}

const ProductsCatalog: React.FC<WithNotificationsProps & WithTranslationProps> = ({notifications, t, i18n}) => {
    const [selectedFilesPictures, setSelectedFilesPictures] = React.useState<any>([])
    const [selectedFilesPDF, setSelectedFilesPDF] = React.useState<any>([])
    const [dataTab, setDataTab] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [model, setModel] = useState<ProductsCatalogsViewModel>({
        id: 0,
        brand: '',
        category: '',
        // currency: Currencies.None,
        name: '',
        datasheetUrl: '',
        datasheetFile: null,
        deliveryTime: 0,
        deliveryUnit: DeliveryUnits.Days,
        description: '',
        filterBrand: 0,
        filterCategory: 0,
        filterProduct: 0,
        image: undefined,
        price: 0,
        quantity: 0,
        sku: '',
        status: ProductStatus.Current,
        unit: Units.Kilogram,
        weight: 0,
        weightUnit: WeightUnits.kg,
        filterBrands: [],
        filterCategories: [],
        filterProducts: [],
        brands: [],
        categories: []
    });

    const getCategories = async () => {
        try{
            setIsLoading(true);
            const language = {
                language: i18n.language
            }
            const response = await axios_helper_async<Options[]>('/SOI/Get-Categories-Options', 'GET', language);
            if(response.isSuccess)
            {
                const general: Options = {
                    value: '',
                    label: t('General.All'),
                }
                if(response.data){
                    response.data.unshift(general);                    
                }
                setModel(prevModel => ({
                    ...prevModel,
                    filterCategories: response.data ?? [general],
                    filterCategory: null
                }));
            }
            else
            {
                notifications.showNotification(t('AxiosAPI.NetworkError'), NotificationType.Error);
            }
        }
        catch(err: any) {
            notifications.showNotification('Categories.Errors.GetCategories', NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }        
    }

    const getBrands = async () => {
        const response = await axios_helper_async<Options[]>('/SOI/Get-Brands-Options', 'GET');
        if(response.isSuccess)
        {
            const general: Options = {
                value: '',
                label: t('General.All'),
            }
            if(response.data) {
                response.data.unshift(general);
            }
            setModel(prevModel => ({
                ...prevModel,
                filterBrands: response.data ?? [general],
                filterBrand: 0
            }));
        }
        else
        {
            notifications.showNotification(t('AxiosAPI.NetworkError'), NotificationType.Error);
        }
    }

    const getProducts = async () => {
        const response = await axios_helper_async<Options[]>('/SOI/Get-Products-Options', 'GET');
        if(response.isSuccess)
        {
            setModel(prevModel => ({
                ...prevModel,
                filterProducts: response.data ?? [],
                filterProduct: 0
            }));
        }
        else
        {
            notifications.showNotification(t('AxiosAPI.NetworkError'), NotificationType.Error);
        }
    }

    const toggleTab = (tab: any, cate: any) => {
        if (activeTab !== tab) {
            setDataTab(cate === "PDF");
            setActiveTab(tab);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        // console.log(name);
        // console.log(value);
        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    };            
    const handleSelectChange = (selectedOption: any, actionMeta: any) => {
        // console.log('cambio')
        setModel(prevModel => ({
            ...prevModel,
            [actionMeta.name]: selectedOption ? Number(selectedOption.value) : 0
        }));
    };

        /**
     * Formats the size
     */
    const formatBytes = (bytes: any, decimals = 2) => {
        if (bytes === 0) return "0 Bytes"
        const k = 1024
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
    }

    const handleAcceptedFiles = (files: any) => {
        files.map((file: any) =>
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
            })
        )
        setSelectedFilesPictures(files)
    }

    const handleAcceptedFilesPDF = (files: any) => {
        files.map((file: any) =>
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
            })
        )
        setSelectedFilesPDF(files)
    }

    const uploadPDF = () => {
        if(selectedFilesPDF.length === 0) {
            notifications.showNotification(t('Catalogs.Products.Warnings.NoFile'), NotificationType.Warning);
            return;
        }
        try {
            // eslint-disable-next-line array-callback-return
            selectedFilesPDF.map((file: any) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const result = event.target?.result;
                    if (result && typeof result === "string") {
                        const binaryString = atob(result.split(',')[1]);
                        const bytes = new Uint8Array(binaryString.length);
                        for (let i = 0; i < binaryString.length; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }
                        model.datasheetFile = bytes;
                    }
                };
                reader.readAsDataURL(file);
                setSelectedFilesPDF([]);
            });
            notifications.showNotification(t('Catalogs.Products.Success.FileUploaded'), NotificationType.Success);
        }
        catch(error) {
            notifications.showNotification(t('Catalogs.Products.Errors.ProcessFileError'), NotificationType.Error);
        }
    }

    const openPDFPreview = () => {
        if(!model.datasheetFile) {
            notifications.showNotification(t('Catalogs.Products.Warnings.NoFile'), NotificationType.Warning);
            return;
        }
        const blob = new Blob([model.datasheetFile], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
            notifications.showNotification(t('Catalogs.Products.Errors.OpenPDF'), NotificationType.Warning);
        }
    };

    useEffect(() => {
        getCategories();
        getBrands();
        getProducts();
    }, [])

    useEffect(() => {
        getCategories();
    }, [i18n.language])

    const test = () => {
        // console.log('test');
    }
    return(
        <React.Fragment>
            <div className='grid grid-cols-12 gap-4'>
                <div className='col-span-12 sm:col-span-4 text-center'>
                    <div className='grid grid-flow-row gap-1 row-span-3'>
                        <div className='grid grid-flow-row row-span-2'>
                            <div>
                                <h6 className="mb-3 text-15 grow">{t('Catalogs.Products.Categories')}</h6>
                            </div>
                            <div className='grid grid-cols-12 gap-2 items-center'>
                                <div className='col-span-10'>
                                    <Select
                                        className="border-slate-200"
                                        name={modelFields.filterCategory}
                                        value={model.filterCategories.find(option => option.value === model.filterCategory?.toString())}
                                        options={model.filterCategories}
                                        placeholder={t('General.SelectItem')}
                                        onChange={handleSelectChange}
                                    />
                                </div>
                                <div className=''>
                                    <button name='btn-rfs-categories' onClick={getCategories} className='btn text-white bg-custom-500'><RefreshCcw className='h-5 w-5'></RefreshCcw></button>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-flow-row row-span-2'>
                            <div>
                                <h6 className="mb-3 text-15 grow">{t('Catalogs.Products.Brands')}</h6>
                            </div>
                            <div className='grid grid-cols-12 gap-2 items-center'>
                                <div className='col-span-10'>
                                    <Select
                                        className="border-slate-200"
                                        name={modelFields.filterBrand}
                                        value={model.filterBrands.find(option => option.value === model.filterBrand.toString())}
                                        options={model.filterBrands}
                                        placeholder={t('General.SelectItem')}
                                        onChange={handleSelectChange}
                                    />
                                </div>
                                <div className=''>
                                    <button name='btn-rfs-brands' onClick={getBrands} className='btn text-white bg-custom-500'><RefreshCcw className='h-5 w-5'></RefreshCcw></button>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-flow-row row-span-2'>
                            <div>
                                <h6 className="mb-3 text-15 grow">{t('Catalogs.Products.Products')}</h6>
                            </div>
                            <div className='grid grid-cols-12 gap-2 items-center'>
                                <div className='col-span-10'>
                                    <Select
                                        className="border-slate-200"
                                        name={modelFields.filterProduct}
                                        value={model.filterProducts.find(option => option.value === model.filterProduct.toString())}
                                        options={model.filterProducts}
                                        placeholder={t('General.SelectItem')}
                                    />
                                </div>
                                <div className=''>
                                    <button name='btn-rfs-products' onClick={getProducts} className='btn text-white bg-custom-500'><RefreshCcw className='h-5 w-5'></RefreshCcw></button>
                                </div>                                
                            </div>
                        </div>
                    </div>
                </div>
                <div className='col-span-12 sm:col-span-8'>
                    <div className='grid grid-flow-row gap-4'>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12'>
                                <h6 className=' text-center'>{t('Catalogs.Products.Name')}</h6>
                                <div className="flex items-center">
                                    <input className="form-input text-left border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"
                                        type='text'
                                        name={modelFields.name}
                                        value={model.name}
                                        id={modelFields.name}
                                        onChange={handleChange}
                                        placeholder={t('Catalogs.Products.Name')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12'>
                                <h6 className=' text-center'>{t('Catalogs.Products.SKU')}</h6>
                                <div className="flex items-center">
                                    <input className="form-input text-left border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"
                                        type='text'
                                        name={modelFields.sku}
                                        value={model.sku}
                                        id={modelFields.sku}
                                        onChange={handleChange}
                                        placeholder={t('Catalogs.Products.SKU')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-6'>
                                <div>
                                    <h6 className="mb-3 text-15 grow text-center">{t('Catalogs.Products.Categories')}</h6>
                                </div>
                                <div className='grid grid-cols-12 gap-2 items-center'>
                                    <div className='col-span-10'>
                                        <Select
                                            className="border-slate-200"
                                            name={modelFields.category}
                                            options={model.categories}
                                            placeholder={t('General.SelectItem')}
                                        />
                                    </div>
                                    <div className=''>
                                        <button name='btn-rfs-categories' className='btn text-white bg-custom-500'><RefreshCcw className='h-5 w-5'></RefreshCcw></button>
                                    </div>
                                </div>
                            </div>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-6'>                                
                                <div>
                                    <h6 className="mb-3 text-15 grow text-center">{t('Catalogs.Products.Brands')}</h6>
                                </div>
                                <div className='grid grid-cols-12 gap-2 items-center'>
                                    <div className='col-span-10'>
                                        <Select
                                            className="border-slate-200"
                                            name={modelFields.brand}
                                            options={model.brands}
                                            placeholder={t('General.SelectItem')}
                                        />
                                    </div>
                                    <div className=''>
                                        <button name='btn-rfs-categories' className='btn text-white bg-custom-500'><RefreshCcw className='h-5 w-5'></RefreshCcw></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-4'>
                                <h6 className=' text-center'>{t('Catalogs.Products.Price')}</h6>
                                <div className="grid grid-flow-col items-center">
                                    {/* <span className="px-3 py-2 col-span-10"><DollarSign className=''></DollarSign></span> */}
                                    <input className="col-span-8 rounded-r-none form-input text-center w-full border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"
                                        type='number'
                                        min={0}
                                        step={0.01}
                                        name={modelFields.price}
                                        value={model.price}
                                        id={modelFields.price}
                                        onChange={handleChange}
                                        placeholder='0.00'
                                    />
                                    {/* <select id={modelFields.currency} name={modelFields.currency} value={model.currency} onChange={handleChange} className="col-span-4 inline-block form-select text-left px-3 py-2 border border-l-0 rounded-l-none border-slate-200 bg-slate-100 rounded-r-md">
                                        <option value={Currencies.MXN}>MXN</option>
                                        <option value={Currencies.USD}>USD</option>
                                    </select> */}
                                </div>
                            </div>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-4'>                                
                                <h6 className=' text-center'>{t('Catalogs.Products.Weight')}</h6>
                                <div className="grid grid-flow-col items-center">
                                    {/* <span className="px-3 py-2"><Weight className=''></Weight></span> */}
                                    <input className="col-span-8 rounded-r-none form-input w-full text-center border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"
                                        type='number'
                                        min={0}
                                        step={0.001}
                                        name={modelFields.weight}
                                        value={model.weight}
                                        id={modelFields.weight}
                                        onChange={handleChange}
                                        placeholder='0.000'
                                    />
                                    <select id={modelFields.weightUnit} name={modelFields.weightUnit} value={model.weightUnit} onChange={handleChange} className="col-span-4 inline-block form-select text-left px-3 py-2 border border-l-0 rounded-l-none border-slate-200 bg-slate-100 rounded-r-md">
                                        <option value={WeightUnits.kg}>kg</option>
                                        <option value={WeightUnits.lb}>lb</option>
                                    </select>
                                </div>
                            </div>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-4'>                                
                                <h6 className=' text-center'>{t('Catalogs.Products.DeliveryTime')}</h6>
                                <div className="grid grid-flow-col items-center">
                                    {/* <span className="px-3 py-2"><Clock className=''></Clock></span> */}
                                    <input className="col-span-6 rounded-r-none form-input text-center w-full border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"
                                        type='number'
                                        min={0}
                                        name={modelFields.deliveryTime}
                                        value={model.deliveryTime}
                                        id={modelFields.deliveryTime}
                                        onChange={handleChange}
                                        placeholder="10" 
                                    />
                                    <select id={modelFields.deliveryUnit} name={modelFields.deliveryUnit} value={model.deliveryUnit} onChange={handleChange} className="col-span-6 inline-block form-select text-left px-3 py-2 border border-l-0 rounded-l-none border-slate-200 bg-slate-100 rounded-r-md">
                                        <option value={DeliveryUnits.Days}>{t('General.Days')}</option>
                                        <option value={DeliveryUnits.Weeks}>{t('General.Weeks')}</option>
                                        <option value={DeliveryUnits.Months}>{t('General.Months')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-6'>
                                <h6 className=' text-center'>{t('Catalogs.Products.Status')}</h6>
                                <div className="flex items-center">
                                    <select id={modelFields.status} name={modelFields.status} value={model.status} onChange={handleChange} className="form-select text-center px-3 py-2 focus:border-custom-500 rounded-r-md bg-slate-100">
                                        <option value={ProductStatus.Current}>{t('General.Current')}</option>
                                        <option value={ProductStatus.Obsolete}>{t('General.Obsolete')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className='grid grid-flow-row col-span-12 sm:col-span-6'>                                
                                <h6 className=' text-center'>{t('Catalogs.Products.Quantity')}</h6>
                                <div className="grid grid-flow-col items-center">
                                    <input className="col-span-10 rounded-r-none form-input text-center w-full border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"
                                        type='number'
                                        min={0}
                                        name={modelFields.quantity}
                                        value={model.quantity}
                                        id={modelFields.quantity}
                                        onChange={handleChange}
                                        placeholder='1'
                                    />
                                    <select id={modelFields.unit} name={modelFields.unit} value={model.unit} onChange={handleChange} className="col-span-2 inline-block form-select text-left px-3 py-2 border border-l-0 rounded-l-none border-slate-200 bg-slate-100 rounded-r-md">
                                        {Object.values(Units).map(unit => (
                                            <option key={unit} value={unit}>{unit}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12'>
                                <h6 className=' text-center'>{t('Catalogs.Products.Description')}</h6>
                                <div className="flex items-center">
                                    <textarea className="form-input text-left border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400"                                        
                                        rows={5}
                                        name={modelFields.description}
                                        value={model.description}
                                        id={modelFields.description}
                                        onChange={handleChange}
                                        placeholder={t('Catalogs.Products.Description')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12 gap-3'>
                                <h6 className=' text-center'>{t('Catalogs.Products.UploadImage')}</h6>
                                <div className="grid grid-flow-row row-span-3 items-center">
                                    <div className="flex items-center justify-center border rounded-md cursor-pointer bg-slate-50 dropzone border-slate-200 dz-clickable">
                                        <Dropzone
                                            onDrop={(acceptedFiles: any) => {
                                                handleAcceptedFiles(acceptedFiles)
                                            }}
                                            accept={{
                                                'image/bmp': ['.bmp'],
                                                'image/jpeg': ['.jpg', '.jpeg'],
                                                'image/png': ['.png'],
                                            }}
                                            maxFiles={1}
                                            multiple={false}
                                            maxSize={5242880}
                                        >
                                            {({ getRootProps, getInputProps }: any) => (
                                                <div
                                                    className="w-full py-5 text-lg text-center dz-message needsclick"
                                                    {...getRootProps()}
                                                >
                                                    <input {...getInputProps()} />
                                                    <div className="mb-3">
                                                        <UploadCloud className="block w-12 h-12 mx-auto text-slate-500 fill-slate-200 "></UploadCloud>
                                                    </div>

                                                    <h5 className="mb-0 font-normal text-slate-500 text-15">{t('Catalogs.Products.Dropzone')}</h5>
                                                </div>
                                            )}
                                        </Dropzone>
                                    </div>
                                    <ul className="mb-0" id="dropzone-preview">
                                        {
                                            (selectedFilesPictures || [])?.map((f: any, i: any) => {
                                                return (
                                                    <li className="mt-2" id="dropzone-preview-list" key={i + "-file"}>
                                                        <div className="border rounded border-slate-200 dark:border-zink-500">
                                                            <div className="flex p-2 items-center">
                                                                <div className="shrink-0 me-3">
                                                                    <div className="p-2 rounded-md w-14 h-14 bg-slate-100 dark:bg-zink-600">
                                                                        <img data-dz-thumbnail className="block w-full h-full rounded-md" src={f.preview} alt={f.name} />
                                                                    </div>
                                                                </div>
                                                                <div className="grow">
                                                                    <div className="pt-1">
                                                                        <h5 className="mb-1 text-15" data-dz-name>{f.name}</h5>
                                                                        <p className="mb-0 text-slate-500 dark:text-zink-200" data-dz-size>{f.formattedSize}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="shrink-0 ms-3">
                                                                    <button data-dz-remove
                                                                        className="px-2 py-1.5 text-xs text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-custom-400/20"
                                                                        onClick={() => {
                                                                            const newImages = [...selectedFilesPictures];
                                                                            newImages.splice(i, 1);
                                                                            setSelectedFilesPictures(newImages);
                                                                        }}
                                                                    >{t('General.Delete')}</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                )
                                            })
                                        }
                                    </ul>
                                </div>
                                <div className='grid grid-cols-12 gap-1'>
                                    <div className='flex col-span-10'>
                                        <button type='button' className='btn bg-custom-500 text-white items-center justify-center w-full'>{t('General.Upload')}</button>
                                    </div>
                                    <div className='flex col-span-2'>
                                        <button type='button' className='btn bg-custom-500 text-white items-center justify-center w-full'>{t('General.Preview')}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 gap-4'>
                            <div className='grid grid-flow-row col-span-12 gap-3 justify-center'>
                                <h6 className=' text-center'>{t('Catalogs.Products.PDF')}</h6>
                                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                                    <div className="col-span-6 xl:col-span-6">
                                        <ul className="flex flex-wrap w-full gap-2 text-sm font-medium text-center filter-btns grow justify-center">
                                            <li>
                                                <Link to="#" data-filter="PDF" className={`inline-block px-4 py-2 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent [&.active]:bg-custom-500 dar:[&.active]:bg-custom-500 [&.active]:text-white dark:[&.active]:text-white hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px] ${activeTab === 1 && "active"}`} onClick={() => toggleTab(1, "PDF")}>PDF</Link>
                                            </li>
                                            <li>
                                                <Link to="#" data-filter="URL" className={`inline-block px-4 py-2 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent [&.active]:bg-custom-500 dar:[&.active]:bg-custom-500 [&.active]:text-white dark:[&.active]:text-white hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px] ${activeTab === 2 && "active"}`} onClick={() => toggleTab(2, "URL")}>URL</Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`grid grid-cols-12 gap-4 ${dataTab ? null:"hidden"}`}>
                            <div className='grid grid-flow-row col-span-12 gap-3'>
                                <h6 className=' text-center'>{t('Catalogs.Products.UploadPDF')}</h6>
                                <div className="grid grid-flow-row row-span-3 items-center">
                                    <div className="flex items-center justify-center border rounded-md cursor-pointer bg-slate-50 dropzone border-slate-200 dz-clickable">
                                        <Dropzone
                                            onDrop={(acceptedFiles: any) => {
                                                handleAcceptedFilesPDF(acceptedFiles)
                                            }}
                                            accept={{
                                                'application/pdf': ['.pdf'],
                                            }}
                                            maxFiles={1}
                                            multiple={false}
                                        >
                                            {({ getRootProps, getInputProps }: any) => (
                                                <div
                                                    className="w-full py-5 text-lg text-center dz-message needsclick"
                                                    {...getRootProps()}
                                                >
                                                    <input {...getInputProps()} />
                                                    <div className="mb-3">
                                                        <UploadCloud className="block w-12 h-12 mx-auto text-slate-500 fill-slate-200 "></UploadCloud>
                                                    </div>

                                                    <h5 className="mb-0 font-normal text-slate-500 text-15">{t('Catalogs.Products.Dropzone')}</h5>
                                                </div>
                                            )}
                                        </Dropzone>
                                    </div>
                                    <ul className="mb-0" id="dropzone-preview">
                                        {
                                            (selectedFilesPDF || [])?.map((f: any, i: any) => {
                                                return (
                                                    <li className="mt-2" id="dropzone-preview-list" key={i + "-file"}>
                                                        <div className="border rounded border-slate-200 dark:border-zink-500">
                                                            <div className="flex p-2 items-center">
                                                                <div className="shrink-0 me-3">
                                                                    <div className="p-2 rounded-md w-14 h-14 bg-slate-100 dark:bg-zink-600 flex items-center justify-center">
                                                                        <File />
                                                                    </div>
                                                                </div>
                                                                <div className="grow">
                                                                    <div className="pt-1">
                                                                        <h5 className="mb-1 text-15" data-dz-name>{f.name}</h5>
                                                                        <p className="mb-0 text-slate-500 dark:text-zink-200" data-dz-size>{f.formattedSize}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="shrink-0 ms-3">
                                                                    <button data-dz-remove
                                                                        className="px-2 py-1.5 text-xs text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-custom-400/20"
                                                                        onClick={() => {
                                                                            const newImages = [...selectedFilesPDF];
                                                                            newImages.splice(i, 1);
                                                                            setSelectedFilesPDF(newImages);
                                                                        }}
                                                                    >{t('General.Delete')}</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                )
                                            })
                                        }
                                    </ul>
                                </div>                                
                                <div className='grid grid-cols-12 gap-1'>
                                    <div className='flex col-span-10'>
                                        <button type='button' onClick={uploadPDF} className='btn bg-custom-500 text-white items-center justify-center w-full'>{t('General.Upload')}</button>
                                    </div>
                                    <div className='flex col-span-2'>
                                        <button type='button' onClick={openPDFPreview} className='btn bg-custom-500 text-white items-center justify-center w-full'>{t('General.Preview')}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`grid grid-cols-12 gap-4 ${!dataTab ? null:"hidden"}`}>
                            <div className='grid grid-flow-row col-span-12 gap-3'>
                                <h6 className=' text-center'>{t('Catalogs.Products.URL')}</h6>                                
                                <div className='grid grid-cols-12 gap-1'>
                                    <div className='flex col-span-10'>
                                        <input className='form-input' 
                                            type='text'
                                            id={modelFields.datasheetUrl}
                                            name={modelFields.datasheetUrl}
                                            value={model.datasheetUrl}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className='flex col-span-2'>
                                        <a href={model.datasheetUrl} role='button' target='_blank' rel="noopener noreferrer" className='btn bg-custom-500 text-white items-center justify-center w-full'>{t('General.Preview')}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='grid grid-cols-12 items-center gap-3'>
                            <div className='col-span-6'>
                                <button type='button' className='btn bg-green-500 text-white w-full'>{t('General.Save')}</button>
                            </div>
                            <div className='col-span-6'>
                                <button type='button' className='btn bg-red-500 text-white w-full'>{t('General.Cancel')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default withTranslation(withNotifications(ProductsCatalog));